import React, { useState, useEffect } from 'react';
import { authService, OrgUser, AuditEntry, Organization as OrgType } from '../services/authService';
import { jiraService, JiraConfig, JiraProject } from '../services/jiraService';
import { useAuth } from '../context/AuthContext';
import {
  Users, Shield, Plus, Edit, Trash2, Check, X, ArrowLeft, History, Lock, Key,
  Building2, RefreshCw, UserPlus, UserX, Eye, EyeOff, Globe, Calendar, 
  ChevronDown, ChevronRight, Search, Download, AlertCircle, CheckCircle, Clock,
  Briefcase, Link2, ExternalLink, Unplug, Plug
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

type AdminTab = 'users' | 'roles' | 'audit_trail' | 'integrations' | 'organizations' | 'global_users' | 'global_audit';

export const Admin: React.FC = () => {
  const navigate = useNavigate();
  const { user: currentUser, isGlobalAdmin, isOrgAdmin, organization, viewingOrganization, setViewingOrganization } = useAuth();
  const [activeTab, setActiveTab] = useState<AdminTab>('users');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // ═══ USER STATE ═══════════════════════════════
  const [users, setUsers] = useState<OrgUser[]>([]);
  const [roles, setRoles] = useState<any[]>([]);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [userForm, setUserForm] = useState({ email: '', first_name: '', last_name: '', password: '', role_id: 0 });
  const [editingUser, setEditingUser] = useState<OrgUser | null>(null);
  const [resetPasswordUserId, setResetPasswordUserId] = useState<number | null>(null);
  const [newPassword, setNewPassword] = useState('');

  // ═══ AUDIT STATE ════════════════════════════════
  const [auditLogs, setAuditLogs] = useState<AuditEntry[]>([]);
  const [auditPage, setAuditPage] = useState(1);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditPages, setAuditPages] = useState(0);
  const [auditActionFilter, setAuditActionFilter] = useState('');
  const [auditEntityTypeFilter, setAuditEntityTypeFilter] = useState('');
  const [auditSourceFilter, setAuditSourceFilter] = useState<'all' | 'rbac' | 'grc'>('all');

  // ═══ ORG STATE (global admin) ════════════════════
  const [orgs, setOrgs] = useState<OrgType[]>([]);
  const [globalUsers, setGlobalUsers] = useState<any[]>([]);
  const [globalAudit, setGlobalAudit] = useState<AuditEntry[]>([]);
  const [globalAuditPage, setGlobalAuditPage] = useState(1);
  const [globalAuditPages, setGlobalAuditPages] = useState(0);
  const [editingOrg, setEditingOrg] = useState<OrgType | null>(null);
  const [orgForm, setOrgForm] = useState({ name: '', contact_email: '', license_type: 'trial', license_expires_at: '', max_users: 10, openai_api_key: '', openai_model: 'gpt-4o' });
  const [isOrgModalOpen, setIsOrgModalOpen] = useState(false);
  const [showApiKey, setShowApiKey] = useState<Record<number, boolean>>({});

  // ═══ ROLE PERMISSIONS STATE ═════════════════════
  const [modules, setModules] = useState<any[]>([]);
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
  const [permissionTypes, setPermissionTypes] = useState<any[]>([]);
  const [rolePermissions, setRolePermissions] = useState<Record<string, Record<number, boolean>>>({});

  // ═══ JIRA INTEGRATION STATE ═════════════════════
  const [jiraConfig, setJiraConfig] = useState<JiraConfig | null>(null);
  const [jiraForm, setJiraForm] = useState({ jira_url: '', jira_email: '', jira_api_token: '', jira_default_project: '' });
  const [jiraProjects, setJiraProjects] = useState<JiraProject[]>([]);
  const [jiraTestResult, setJiraTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [jiraLoading, setJiraLoading] = useState(false);
  const [isCreateRoleOpen, setIsCreateRoleOpen] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');

  // Load organizations list on mount for global admin (needed for org selector)
  useEffect(() => {
    if (isGlobalAdmin) {
      authService.getOrganizations().then(o => setOrgs(o)).catch(() => {});
    }
  }, [isGlobalAdmin]);

  useEffect(() => {
    if (isOrgAdmin || isGlobalAdmin) {
      fetchData();
    }
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      if (activeTab === 'users') {
        const [u, r] = await Promise.all([authService.getUsers(), authService.getRolesList()]);
        setUsers(u);
        setRoles(r);
      } else if (activeTab === 'roles') {
        const [r, m] = await Promise.all([authService.getRolesList(), authService.getModules()]);
        setRoles(r);
        setModules(m);
      } else if (activeTab === 'audit_trail') {
        const res = await authService.getAuditLog(auditPage, 50, auditActionFilter || undefined, undefined, auditEntityTypeFilter || undefined, auditSourceFilter);
        setAuditLogs(res.data || []);
        setAuditTotal(res.total);
        setAuditPages(res.pages);
      } else if (activeTab === 'organizations' && isGlobalAdmin) {
        const o = await authService.getOrganizations();
        setOrgs(o);
      } else if (activeTab === 'global_users' && isGlobalAdmin) {
        const u = await authService.getGlobalUsers();
        setGlobalUsers(u);
      } else if (activeTab === 'global_audit' && isGlobalAdmin) {
        const res = await authService.getGlobalAuditLog(globalAuditPage, 50);
        setGlobalAudit(res.data || []);
        setGlobalAuditPages(res.pages);
      } else if (activeTab === 'integrations') {
        const config = await jiraService.getConfig();
        setJiraConfig(config);
        if (config) {
          setJiraForm({
            jira_url: config.jira_url || '',
            jira_email: config.jira_email || '',
            jira_api_token: '',
            jira_default_project: config.jira_default_project || ''
          });
          if (config.is_configured) {
            try {
              const projects = await jiraService.getProjects();
              setJiraProjects(projects);
            } catch { /* ignore */ }
          }
        }
      }
    } catch (e: any) {
      setError(e.message || 'Failed to load data');
    }
    setLoading(false);
  };

  const showSuccess = (msg: string) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(''), 3000);
  };

  // ═══ USER ACTIONS ════════════════════════════════
  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res = await authService.createUser({
        email: userForm.email,
        first_name: userForm.first_name,
        last_name: userForm.last_name,
        password: userForm.password,
        role_id: userForm.role_id || undefined,
      });
      if (res.success) {
        showSuccess('User created successfully');
        setIsUserModalOpen(false);
        fetchData();
      } else {
        setError(res.error || 'Failed to create user');
      }
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleToggleUser = async (userId: number, currentActive: number) => {
    try {
      await authService.toggleUser(userId, !currentActive);
      showSuccess(currentActive ? 'User deactivated' : 'User activated');
      fetchData();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleResetPassword = async () => {
    if (!resetPasswordUserId || newPassword.length < 6) return;
    try {
      const res = await authService.resetPassword(resetPasswordUserId, newPassword);
      if (res.success) {
        showSuccess('Password reset successfully');
        setResetPasswordUserId(null);
        setNewPassword('');
      } else {
        setError(res.error || 'Failed');
      }
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleAssignRole = async (userId: number, roleId: number) => {
    try {
      await authService.assignRole(userId, roleId);
      showSuccess('Role assigned');
      fetchData();
    } catch (e: any) {
      setError(e.message);
    }
  };

  // ═══ ROLE PERMISSIONS ═══════════════════════════
  const loadRolePermissions = async (roleId: number) => {
    setSelectedRoleId(roleId);
    try {
      const res = await authService.getPermissionsMatrix(roleId);
      setPermissionTypes(res.permission_types || []);
      setRolePermissions(res.permissions || {});
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleSavePermissions = async () => {
    if (!selectedRoleId) return;
    const perms: Array<{ module_id: number; section_id?: number; permission_type_id: number }> = [];
    Object.entries(rolePermissions).forEach(([key, ptMap]) => {
      const [moduleId, sectionId] = key.split('-').map(Number);
      Object.entries(ptMap).forEach(([ptId, granted]) => {
        if (granted) {
          perms.push({ module_id: moduleId, section_id: sectionId || undefined, permission_type_id: Number(ptId) });
        }
      });
    });
    try {
      await authService.setPermissions(selectedRoleId, perms);
      showSuccess('Permissions saved');
    } catch (e: any) {
      setError(e.message);
    }
  };

  const togglePermission = (moduleId: number, sectionId: number, ptId: number) => {
    const key = `${moduleId}-${sectionId}`;
    setRolePermissions(prev => ({
      ...prev,
      [key]: { ...prev[key], [ptId]: !prev[key]?.[ptId] },
    }));
  };

  const handleCreateRole = async () => {
    if (!newRoleName.trim()) return;
    try {
      const res = await authService.createRole(newRoleName);
      if (res.success) {
        showSuccess('Role created');
        setIsCreateRoleOpen(false);
        setNewRoleName('');
        fetchData();
      }
    } catch (e: any) {
      setError(e.message);
    }
  };

  // ═══ ORG ACTIONS (global admin) ═════════════════
  const handleUpdateOrg = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingOrg) return;
    try {
      const res = await authService.updateOrganization(editingOrg.id, orgForm);
      if (res.success) {
        // If API key provided, update it separately
        if (orgForm.openai_api_key !== (editingOrg.openai_api_key || '')) {
          await authService.setOrgApiKey(editingOrg.id, orgForm.openai_api_key, orgForm.openai_model);
        }
        showSuccess('Organization updated');
        setIsOrgModalOpen(false);
        fetchData();
      }
    } catch (e: any) {
      setError(e.message);
    }
  };

  const openOrgModal = (org: OrgType) => {
    setEditingOrg(org);
    setOrgForm({
      name: org.name,
      contact_email: org.contact_email || '',
      license_type: org.license_type || 'trial',
      license_expires_at: org.license_expires_at?.split(' ')[0] || '',
      max_users: org.max_users || 10,
      openai_api_key: org.openai_api_key || '',
      openai_model: org.openai_model || 'gpt-4o',
    });
    setIsOrgModalOpen(true);
  };

  // ═══ JIRA ACTIONS ═══════════════════════════════
  const handleSaveJiraConfig = async (e: React.FormEvent) => {
    e.preventDefault();
    setJiraLoading(true);
    setJiraTestResult(null);
    try {
      const res = await jiraService.saveConfig(jiraForm);
      if (res.success) {
        showSuccess('Jira configuration saved');
        fetchData();
      } else {
        setError(res.error || 'Failed to save Jira config');
      }
    } catch (e: any) {
      setError(e.message);
    }
    setJiraLoading(false);
  };

  const handleTestJiraConnection = async () => {
    setJiraLoading(true);
    setJiraTestResult(null);
    try {
      const res = await jiraService.testConnection();
      setJiraTestResult({
        success: res.success,
        message: res.success 
          ? `Connected as ${res.user?.display_name || 'Unknown'} (${res.user?.email || ''})` 
          : res.error || 'Connection failed'
      });
      if (res.success) {
        const projects = await jiraService.getProjects();
        setJiraProjects(projects);
      }
    } catch (e: any) {
      setJiraTestResult({ success: false, message: e.message });
    }
    setJiraLoading(false);
  };

  const handleDisconnectJira = async () => {
    if (!confirm('Are you sure you want to disconnect Jira?')) return;
    setJiraLoading(true);
    try {
      await jiraService.disconnect();
      setJiraConfig(null);
      setJiraForm({ jira_url: '', jira_email: '', jira_api_token: '', jira_default_project: '' });
      setJiraProjects([]);
      setJiraTestResult(null);
      showSuccess('Jira disconnected');
    } catch (e: any) {
      setError(e.message);
    }
    setJiraLoading(false);
  };

  // ═══ UI HELPERS ═════════════════════════════════
  const formatDate = (d: string | null) => d ? new Date(d).toLocaleString() : '—';
  const actionColor = (action: string) => {
    if (action.includes('LOGIN_SUCCESS') || action.includes('CREATED') || action.includes('ACTIVATED')) return 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20';
    if (action.includes('FAILED') || action.includes('BLOCKED') || action.includes('DEACTIVATED')) return 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20';
    if (action.includes('UPDATED') || action.includes('CHANGED') || action.includes('RESET')) return 'text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20';
    return 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20';
  };

  // ═══ CHECK ACCESS ══════════════════════════════
  if (!isOrgAdmin && !isGlobalAdmin) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-8 text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-red-800 dark:text-red-300 mb-2">Access Denied</h2>
          <p className="text-red-600 dark:text-red-400">You need Organization Admin or Global Admin privileges to access this panel.</p>
          <button onClick={() => navigate('/')} className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700">Back to Dashboard</button>
        </div>
      </div>
    );
  }

  const tabs: { id: AdminTab; label: string; icon: React.ElementType; globalOnly?: boolean }[] = [
    { id: 'users', label: 'Users', icon: Users },
    { id: 'roles', label: 'Roles & Permissions', icon: Lock },
    { id: 'audit_trail', label: 'Audit Trail', icon: History },
    { id: 'integrations', label: 'Integrations', icon: Link2 },
    ...(isGlobalAdmin ? [
      { id: 'organizations' as AdminTab, label: 'Organizations', icon: Building2, globalOnly: true },
      { id: 'global_users' as AdminTab, label: 'All Users', icon: Globe, globalOnly: true },
      { id: 'global_audit' as AdminTab, label: 'Global Audit', icon: Eye, globalOnly: true },
    ] : []),
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Admin Panel</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {isGlobalAdmin ? (
                <span className="flex items-center gap-1"><Globe className="w-3.5 h-3.5" /> Global Administrator — {currentUser?.email}</span>
              ) : (
                <span className="flex items-center gap-1"><Building2 className="w-3.5 h-3.5" /> {organization?.name} — Org Admin</span>
              )}
            </p>
          </div>
        </div>
        
        {/* Organization Selector for Global Admin */}
        {isGlobalAdmin && (
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 bg-purple-50 dark:bg-purple-900/30 px-4 py-2 rounded-lg border border-purple-200 dark:border-purple-700">
              <Briefcase className="w-4 h-4 text-purple-600 dark:text-purple-400" />
              <span className="text-sm text-purple-700 dark:text-purple-300 font-medium">
                Viewing:
              </span>
              <select
                value={viewingOrganization?.id || ''}
                onChange={(e) => {
                  const orgId = parseInt(e.target.value);
                  if (orgId) {
                    const org = orgs.find(o => o.id === orgId);
                    if (org) setViewingOrganization({ id: org.id, name: org.name, slug: org.slug, license_type: org.license_type, license_expires_at: org.license_expires_at || '' });
                  } else {
                    setViewingOrganization(null);
                  }
                }}
                className="text-sm bg-white dark:bg-gray-700 border-0 rounded px-2 py-1 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500"
              >
                <option value="">All Organizations</option>
                {orgs.map(org => (
                  <option key={org.id} value={org.id}>{org.name}</option>
                ))}
              </select>
            </div>
            {viewingOrganization && (
              <button
                onClick={() => setViewingOrganization(null)}
                className="text-xs px-2 py-1 bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded hover:bg-gray-300 dark:hover:bg-gray-600"
              >
                Clear
              </button>
            )}
          </div>
        )}
      </div>

      {/* Alerts */}
      {error && (
        <div className="mb-4 flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <span className="text-sm text-red-700 dark:text-red-300">{error}</span>
          <button onClick={() => setError('')} className="ml-auto text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>
        </div>
      )}
      {success && (
        <div className="mb-4 flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
          <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
          <span className="text-sm text-green-700 dark:text-green-300">{success}</span>
        </div>
      )}

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-500/25'
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
            } ${tab.globalOnly ? 'ring-1 ring-purple-300 dark:ring-purple-700' : ''}`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
            {tab.globalOnly && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300">Admin</span>}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-6 h-6 animate-spin text-indigo-500" />
          <span className="ml-2 text-gray-500">Loading...</span>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 shadow-xl rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">

          {/* ═══ USERS TAB ═══════════════════════ */}
          {activeTab === 'users' && (
            <div>
              <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <Users className="w-5 h-5 text-indigo-500" /> Organization Users ({users.length})
                </h2>
                <button onClick={() => { setEditingUser(null); setUserForm({ email: '', first_name: '', last_name: '', password: '', role_id: 0 }); setIsUserModalOpen(true); }}
                  className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors text-sm">
                  <UserPlus className="w-4 h-4" /> Add User
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-900/50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">User</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Role</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Last Login</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {users.map(u => (
                      <tr key={u.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{u.first_name} {u.last_name}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">{u.email}</div>
                        </td>
                        <td className="px-6 py-4">
                          <select
                            className="text-xs border rounded-md px-2 py-1 bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                            value={u.role_slugs?.split(', ')[0] || ''}
                            onChange={(e) => {
                              const role = roles.find(r => r.slug === e.target.value);
                              if (role) handleAssignRole(u.id, role.id);
                            }}
                          >
                            <option value="">No role</option>
                            {roles.map(r => (
                              <option key={r.id} value={r.slug}>{r.name}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-6 py-4">
                          {u.is_active ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
                              <CheckCircle className="w-3 h-3" /> Active
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300">
                              <X className="w-3 h-3" /> Inactive
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-xs text-gray-500 dark:text-gray-400">{formatDate(u.last_login)}</td>
                        <td className="px-6 py-4 text-right space-x-2">
                          <button onClick={() => setResetPasswordUserId(u.id)} title="Reset Password"
                            className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-600 text-amber-500"><Key className="w-4 h-4" /></button>
                          <button onClick={() => handleToggleUser(u.id, u.is_active)} title={u.is_active ? 'Deactivate' : 'Activate'}
                            className={`p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-600 ${u.is_active ? 'text-red-500' : 'text-green-500'}`}>
                            {u.is_active ? <UserX className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                          </button>
                        </td>
                      </tr>
                    ))}
                    {users.length === 0 && (
                      <tr><td colSpan={5} className="px-6 py-8 text-center text-gray-500">No users found</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ═══ ROLES TAB ═══════════════════════ */}
          {activeTab === 'roles' && (
            <div>
              <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <Lock className="w-5 h-5 text-indigo-500" /> Roles & Permissions
                </h2>
                <button onClick={() => setIsCreateRoleOpen(true)}
                  className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 text-sm">
                  <Plus className="w-4 h-4" /> Create Role
                </button>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-4 divide-y lg:divide-y-0 lg:divide-x divide-gray-200 dark:divide-gray-700">
                {/* Role List */}
                <div className="lg:col-span-1 p-4 space-y-2">
                  <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-3">Select Role</h3>
                  {roles.map(r => (
                    <button
                      key={r.id}
                      onClick={() => loadRolePermissions(r.id)}
                      className={`w-full text-left px-3 py-2.5 rounded-lg flex items-center justify-between transition-colors text-sm ${
                        selectedRoleId === r.id 
                          ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-700'
                          : 'hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      <span>{r.name}</span>
                      <span className="text-xs text-gray-400">{r.user_count || 0} users</span>
                    </button>
                  ))}
                </div>
                {/* Permission Matrix */}
                <div className="lg:col-span-3 p-4">
                  {selectedRoleId ? (
                    <div>
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="font-medium text-gray-900 dark:text-gray-100">Permission Matrix</h3>
                        <button onClick={handleSavePermissions} className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 flex items-center gap-2">
                          <Check className="w-4 h-4" /> Save Permissions
                        </button>
                      </div>
                      {permissionTypes.length > 0 && (
                        <div className="overflow-x-auto">
                          <table className="min-w-full text-sm">
                            <thead>
                              <tr className="border-b dark:border-gray-700">
                                <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Module / Section</th>
                                {permissionTypes.slice(0, 5).map(pt => (
                                  <th key={pt.id} className="py-2 px-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 capitalize">
                                    {pt.name}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {modules.map(m => (
                                <React.Fragment key={m.id}>
                                  {m.sections?.map((s: any) => (
                                    <tr key={`${m.id}-${s.id}`} className="border-b dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/30">
                                      <td className="py-2 px-3 text-gray-700 dark:text-gray-300">
                                        <span className="text-xs text-gray-400">{m.name} /</span> {s.name}
                                      </td>
                                      {permissionTypes.slice(0, 5).map(pt => {
                                        const key = `${m.id}-${s.id}`;
                                        const checked = rolePermissions[key]?.[pt.id] || false;
                                        return (
                                          <td key={pt.id} className="py-2 px-3 text-center">
                                            <input
                                              type="checkbox"
                                              checked={checked}
                                              onChange={() => togglePermission(m.id, s.id, pt.id)}
                                              className="rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500"
                                            />
                                          </td>
                                        );
                                      })}
                                    </tr>
                                  ))}
                                </React.Fragment>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                      {permissionTypes.length === 0 && (
                        <p className="text-sm text-gray-500 py-4">No permissions configured for this role yet.</p>
                      )}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                      <Shield className="w-12 h-12 mb-3" />
                      <p>Select a role to manage permissions</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ═══ AUDIT TRAIL TAB ═════════════════ */}
          {activeTab === 'audit_trail' && (
            <div>
              <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center flex-wrap gap-3">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <History className="w-5 h-5 text-indigo-500" /> Audit Trail ({auditTotal} entries)
                </h2>
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Filter by action..."
                      value={auditActionFilter}
                      onChange={(e) => setAuditActionFilter(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && fetchData()}
                      className="pl-9 pr-3 py-2 text-sm border rounded-lg bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 w-40"
                    />
                  </div>
                  <select
                    value={auditEntityTypeFilter}
                    onChange={(e) => { setAuditEntityTypeFilter(e.target.value); }}
                    className="px-3 py-2 text-sm border rounded-lg bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                  >
                    <option value="">All Entities</option>
                    <option value="user">Users</option>
                    <option value="role">Roles</option>
                    <option value="session">Sessions</option>
                    <option value="framework">Frameworks</option>
                    <option value="control">Controls</option>
                    <option value="risk">Risks</option>
                    <option value="asset">Assets</option>
                    <option value="document">Documents</option>
                    <option value="incident">Incidents</option>
                    <option value="supplier">Suppliers</option>
                    <option value="audit">Audits</option>
                  </select>
                  <select
                    value={auditSourceFilter}
                    onChange={(e) => setAuditSourceFilter(e.target.value as any)}
                    className="px-3 py-2 text-sm border rounded-lg bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
                  >
                    <option value="all">All Sources</option>
                    <option value="rbac">Auth/RBAC</option>
                    <option value="grc">GRC Activities</option>
                  </select>
                  <button onClick={fetchData} className="p-2 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-200 dark:hover:bg-indigo-900/50">
                    <RefreshCw className="w-4 h-4" />
                  </button>
                  <button 
                    onClick={() => {
                      setAuditActionFilter('');
                      setAuditEntityTypeFilter('');
                      setAuditSourceFilter('all');
                      setTimeout(fetchData, 0);
                    }} 
                    className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                  >
                    Clear
                  </button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-900/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Time</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">User</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Source</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Action</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Entity</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Details</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">IP</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {auditLogs.map((log, idx) => (
                      <tr key={`${log.source}-${log.id}-${idx}`} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 text-sm">
                        <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">
                          <Clock className="w-3 h-3 inline mr-1" />{formatDate(log.created_at)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-gray-700 dark:text-gray-300">
                          {log.first_name ? `${log.first_name} ${log.last_name}` : log.username || '—'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                            log.source === 'grc' 
                              ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300' 
                              : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                          }`}>
                            {log.source === 'grc' ? 'GRC' : 'Auth'}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${actionColor(log.action)}`}>
                            {log.action}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-gray-600 dark:text-gray-400 text-xs">
                          {log.entity_type}{log.entity_name ? `: ${log.entity_name}` : ''}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400 max-w-xs truncate">
                          {log.details || '—'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-400 font-mono">{log.ip_address}</td>
                      </tr>
                    ))}
                    {auditLogs.length === 0 && (
                      <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">No audit entries found</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              {auditPages > 1 && (
                <div className="p-4 border-t dark:border-gray-700 flex items-center justify-between">
                  <span className="text-sm text-gray-500">Page {auditPage} of {auditPages}</span>
                  <div className="flex gap-2">
                    <button disabled={auditPage <= 1} onClick={() => { setAuditPage(p => p - 1); fetchData(); }}
                      className="px-3 py-1 text-sm border rounded disabled:opacity-50 dark:border-gray-600 dark:text-gray-300">Prev</button>
                    <button disabled={auditPage >= auditPages} onClick={() => { setAuditPage(p => p + 1); fetchData(); }}
                      className="px-3 py-1 text-sm border rounded disabled:opacity-50 dark:border-gray-600 dark:text-gray-300">Next</button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ═══ INTEGRATIONS TAB ═══════════════════ */}
          {activeTab === 'integrations' && (
            <div>
              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <Link2 className="w-5 h-5 text-blue-500" /> External Integrations
                </h2>
              </div>
              
              <div className="p-4 space-y-6">
                {/* Jira Integration */}
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <div className="p-4 bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/20 border-b border-blue-200 dark:border-blue-800">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center">
                          <svg viewBox="0 0 24 24" className="w-6 h-6 text-white" fill="currentColor">
                            <path d="M11.571 11.513H0a5.218 5.218 0 0 0 5.232 5.215h2.13v2.057A5.215 5.215 0 0 0 12.575 24V12.518a1.005 1.005 0 0 0-1.005-1.005ZM5.981 6.24H0a5.22 5.22 0 0 0 5.232 5.215h.749v2.058A5.218 5.218 0 0 0 11.194 18.7V7.246a1.01 1.01 0 0 0-.999-1.006ZM5.232 0A5.218 5.218 0 0 0 0 5.232h5.232a1 1 0 0 0 1.005-1.005V0Z"/>
                          </svg>
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900 dark:text-gray-100">Jira Integration</h3>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            Connect to Jira to link evidence and create tasks from incidents/risks
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {jiraConfig?.is_configured ? (
                          <span className="flex items-center gap-1 text-sm text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/30 px-3 py-1 rounded-full">
                            <CheckCircle className="w-4 h-4" /> Connected
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-3 py-1 rounded-full">
                            <Unplug className="w-4 h-4" /> Not Connected
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <form onSubmit={handleSaveJiraConfig} className="p-4 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Jira URL
                        </label>
                        <input 
                          type="url" 
                          value={jiraForm.jira_url}
                          onChange={(e) => setJiraForm({ ...jiraForm, jira_url: e.target.value })}
                          placeholder="https://your-company.atlassian.net"
                          className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Jira Email
                        </label>
                        <input 
                          type="email" 
                          value={jiraForm.jira_email}
                          onChange={(e) => setJiraForm({ ...jiraForm, jira_email: e.target.value })}
                          placeholder="your-email@company.com"
                          className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 text-sm"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          API Token {jiraConfig?.is_configured && <span className="text-gray-400">(leave blank to keep current)</span>}
                        </label>
                        <input 
                          type="password" 
                          value={jiraForm.jira_api_token}
                          onChange={(e) => setJiraForm({ ...jiraForm, jira_api_token: e.target.value })}
                          placeholder={jiraConfig?.is_configured ? '••••••••••••••••' : 'Paste your Jira API token'}
                          className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 text-sm font-mono"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          <a href="https://id.atlassian.com/manage-profile/security/api-tokens" target="_blank" rel="noopener noreferrer" 
                            className="text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1">
                            Create API Token <ExternalLink className="w-3 h-3" />
                          </a>
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Default Project
                        </label>
                        {jiraProjects.length > 0 ? (
                          <select
                            value={jiraForm.jira_default_project}
                            onChange={(e) => setJiraForm({ ...jiraForm, jira_default_project: e.target.value })}
                            className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 text-sm"
                          >
                            <option value="">Select a project...</option>
                            {jiraProjects.map(p => (
                              <option key={p.key} value={p.key}>{p.name} ({p.key})</option>
                            ))}
                          </select>
                        ) : (
                          <input 
                            type="text" 
                            value={jiraForm.jira_default_project}
                            onChange={(e) => setJiraForm({ ...jiraForm, jira_default_project: e.target.value })}
                            placeholder="e.g., PROJ"
                            className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 text-sm"
                          />
                        )}
                      </div>
                    </div>
                    
                    {/* Test Result */}
                    {jiraTestResult && (
                      <div className={`p-3 rounded-lg flex items-center gap-2 ${
                        jiraTestResult.success 
                          ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300' 
                          : 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                      }`}>
                        {jiraTestResult.success ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
                        {jiraTestResult.message}
                      </div>
                    )}
                    
                    {/* Actions */}
                    <div className="flex items-center justify-between pt-2 border-t dark:border-gray-700">
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={handleTestJiraConnection}
                          disabled={jiraLoading || !jiraForm.jira_url || !jiraForm.jira_email}
                          className="px-4 py-2 text-sm border border-blue-300 dark:border-blue-600 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/30 disabled:opacity-50 flex items-center gap-2"
                        >
                          <Plug className="w-4 h-4" /> Test Connection
                        </button>
                        {jiraConfig?.is_configured && (
                          <button
                            type="button"
                            onClick={handleDisconnectJira}
                            disabled={jiraLoading}
                            className="px-4 py-2 text-sm border border-red-300 dark:border-red-600 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-50 flex items-center gap-2"
                          >
                            <Unplug className="w-4 h-4" /> Disconnect
                          </button>
                        )}
                      </div>
                      <button
                        type="submit"
                        disabled={jiraLoading || !jiraForm.jira_url || !jiraForm.jira_email}
                        className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                      >
                        {jiraLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                        Save Configuration
                      </button>
                    </div>
                  </form>
                </div>
                
                {/* More integrations can be added here in the future */}
                <div className="text-center py-8 text-gray-400 dark:text-gray-600">
                  <p className="text-sm">More integrations coming soon: GitHub, GitLab, ServiceNow, Slack...</p>
                </div>
              </div>
            </div>
          )}

          {/* ═══ ORGANIZATIONS TAB (global admin) ═══ */}
          {activeTab === 'organizations' && isGlobalAdmin && (
            <div>
              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-purple-500" /> Organizations ({orgs.length})
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
                {orgs.map(org => (
                  <div key={org.id} className="border dark:border-gray-700 rounded-xl p-5 hover:shadow-lg transition-shadow bg-white dark:bg-gray-800">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-gray-900 dark:text-gray-100">{org.name}</h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{org.slug}</p>
                      </div>
                      <button onClick={() => openOrgModal(org)} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-indigo-500">
                        <Edit className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">License:</span>
                        <span className={`font-medium px-2 py-0.5 rounded-full text-xs ${org.license_type === 'enterprise' ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300' : 'bg-gray-100 text-gray-600'}`}>
                          {org.license_type || 'trial'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">Expires:</span>
                        <span className="text-gray-700 dark:text-gray-300 text-xs">{org.license_expires_at?.split(' ')[0] || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">Users:</span>
                        <span className="text-gray-700 dark:text-gray-300">{org.user_count} / {org.max_users}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500 dark:text-gray-400">OpenAI Key:</span>
                        <span className="flex items-center gap-1">
                          {org.openai_api_key ? (
                            <>
                              <span className="font-mono text-xs text-gray-600 dark:text-gray-400">
                                {showApiKey[org.id] ? org.openai_api_key : `${org.openai_api_key?.substring(0, 8)}...`}
                              </span>
                              <button onClick={() => setShowApiKey(p => ({ ...p, [org.id]: !p[org.id] }))} className="text-gray-400 hover:text-gray-600">
                                {showApiKey[org.id] ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                              </button>
                            </>
                          ) : (
                            <span className="text-xs text-gray-400">Not set</span>
                          )}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">Model:</span>
                        <span className="text-gray-700 dark:text-gray-300 text-xs font-mono">{org.openai_model || 'default'}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ═══ GLOBAL USERS TAB ════════════════ */}
          {activeTab === 'global_users' && isGlobalAdmin && (
            <div>
              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <Globe className="w-5 h-5 text-purple-500" /> All Users Across Organizations ({globalUsers.length})
                </h2>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-900/50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">User</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Organization</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Roles</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Last Login</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {globalUsers.map((u: any) => (
                      <tr key={u.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {u.first_name} {u.last_name}
                            {u.is_global_admin ? <span className="ml-2 text-xs px-1.5 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">Global Admin</span> : null}
                          </div>
                          <div className="text-xs text-gray-500">{u.email}</div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">{u.organizations || '—'}</td>
                        <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">{u.roles || '—'}</td>
                        <td className="px-6 py-4">
                          {u.is_active ? (
                            <span className="inline-flex items-center gap-1 text-xs text-green-600 dark:text-green-400"><CheckCircle className="w-3 h-3" /> Active</span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs text-red-600 dark:text-red-400"><X className="w-3 h-3" /> Inactive</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-xs text-gray-500">{formatDate(u.last_login)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ═══ GLOBAL AUDIT TAB ════════════════ */}
          {activeTab === 'global_audit' && isGlobalAdmin && (
            <div>
              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <Eye className="w-5 h-5 text-purple-500" /> Global Audit Log
                </h2>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-900/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Time</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Org</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">User</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Action</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Details</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {globalAudit.map(log => (
                      <tr key={log.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 text-sm">
                        <td className="px-4 py-3 text-xs text-gray-500">{formatDate(log.created_at)}</td>
                        <td className="px-4 py-3 text-xs text-gray-600 dark:text-gray-400">{log.org_name || '—'}</td>
                        <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                          {log.first_name ? `${log.first_name} ${log.last_name}` : log.username || '—'}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${actionColor(log.action)}`}>{log.action}</span>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500 max-w-xs truncate">{log.details || '—'}</td>
                      </tr>
                    ))}
                    {globalAudit.length === 0 && (
                      <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-500">No entries</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              {globalAuditPages > 1 && (
                <div className="p-4 border-t dark:border-gray-700 flex items-center justify-between">
                  <span className="text-sm text-gray-500">Page {globalAuditPage} of {globalAuditPages}</span>
                  <div className="flex gap-2">
                    <button disabled={globalAuditPage <= 1} onClick={() => { setGlobalAuditPage(p => p - 1); fetchData(); }}
                      className="px-3 py-1 text-sm border rounded disabled:opacity-50 dark:border-gray-600">Prev</button>
                    <button disabled={globalAuditPage >= globalAuditPages} onClick={() => { setGlobalAuditPage(p => p + 1); fetchData(); }}
                      className="px-3 py-1 text-sm border rounded disabled:opacity-50 dark:border-gray-600">Next</button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ═══ CREATE USER MODAL ═══════════════════ */}
      {isUserModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-md w-full p-6 shadow-2xl">
            <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-indigo-500" /> Create User
            </h3>
            <form onSubmit={handleCreateUser} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email *</label>
                <input type="email" required value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 text-sm" placeholder="user@company.com" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">First Name *</label>
                  <input type="text" required value={userForm.first_name} onChange={(e) => setUserForm({ ...userForm, first_name: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Last Name</label>
                  <input type="text" value={userForm.last_name} onChange={(e) => setUserForm({ ...userForm, last_name: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Password *</label>
                <input type="password" required minLength={6} value={userForm.password} onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 text-sm" placeholder="Min 6 characters" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Role</label>
                <select value={userForm.role_id} onChange={(e) => setUserForm({ ...userForm, role_id: Number(e.target.value) })}
                  className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 text-sm">
                  <option value={0}>No role</option>
                  {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setIsUserModalOpen(false)} className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 border dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700">Cancel</button>
                <button type="submit" className="px-4 py-2 text-sm text-white bg-indigo-600 rounded-lg hover:bg-indigo-700">Create User</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ═══ RESET PASSWORD MODAL ═══════════════ */}
      {resetPasswordUserId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-sm w-full p-6 shadow-2xl">
            <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <Key className="w-5 h-5 text-amber-500" /> Reset Password
            </h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">New Password</label>
              <input type="password" minLength={6} value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 text-sm" placeholder="Min 6 characters" />
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => { setResetPasswordUserId(null); setNewPassword(''); }} className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 border dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700">Cancel</button>
              <button onClick={handleResetPassword} className="px-4 py-2 text-sm text-white bg-amber-600 rounded-lg hover:bg-amber-700">Reset Password</button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ CREATE ROLE MODAL ═══════════════════ */}
      {isCreateRoleOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-sm w-full p-6 shadow-2xl">
            <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">Create Role</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Role Name</label>
              <input type="text" value={newRoleName} onChange={(e) => setNewRoleName(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 text-sm" placeholder="e.g. Security Analyst" />
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setIsCreateRoleOpen(false)} className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 border dark:border-gray-600 rounded-lg">Cancel</button>
              <button onClick={handleCreateRole} className="px-4 py-2 text-sm text-white bg-indigo-600 rounded-lg hover:bg-indigo-700">Create</button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ EDIT ORG MODAL (global admin) ═══════ */}
      {isOrgModalOpen && editingOrg && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-lg w-full p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-purple-500" /> Edit: {editingOrg.name}
            </h3>
            <form onSubmit={handleUpdateOrg} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name</label>
                <input type="text" value={orgForm.name} onChange={(e) => setOrgForm({ ...orgForm, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Contact Email</label>
                <input type="email" value={orgForm.contact_email} onChange={(e) => setOrgForm({ ...orgForm, contact_email: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">License Type</label>
                  <select value={orgForm.license_type} onChange={(e) => setOrgForm({ ...orgForm, license_type: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 text-sm">
                    <option value="trial">Trial</option>
                    <option value="basic">Basic</option>
                    <option value="professional">Professional</option>
                    <option value="enterprise">Enterprise</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">License Expires</label>
                  <input type="date" value={orgForm.license_expires_at} onChange={(e) => setOrgForm({ ...orgForm, license_expires_at: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Max Users</label>
                <input type="number" min="1" value={orgForm.max_users} onChange={(e) => setOrgForm({ ...orgForm, max_users: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 text-sm" />
              </div>
              <div className="border-t dark:border-gray-700 pt-4">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                  <Key className="w-4 h-4 text-amber-500" /> OpenAI Configuration
                </h4>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">API Key</label>
                  <input type="password" value={orgForm.openai_api_key} onChange={(e) => setOrgForm({ ...orgForm, openai_api_key: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 text-sm font-mono" placeholder="sk-..." />
                </div>
                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Model</label>
                  <select value={orgForm.openai_model} onChange={(e) => setOrgForm({ ...orgForm, openai_model: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 text-sm">
                    <option value="gpt-4o">GPT-4o</option>
                    <option value="gpt-4o-mini">GPT-4o Mini</option>
                    <option value="gpt-4-turbo">GPT-4 Turbo</option>
                    <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
                  </select>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setIsOrgModalOpen(false)} className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 border dark:border-gray-600 rounded-lg">Cancel</button>
                <button type="submit" className="px-4 py-2 text-sm text-white bg-purple-600 rounded-lg hover:bg-purple-700">Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
